import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonButtonRowWidget, CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonSelectFieldWidget, CommonTextField, CommonTextFieldWidget } from '@eforall/common';
import { DashboardDialogFramePart, DialogContext } from '@eforall/dashboard';
import { DialogMode, VolunteerTopicForm } from '@interfaces';
import { DomainDataService } from '../../../../services';
import { VolunteerDialogDataService } from '../volunteer-dialog';

export interface VolunteerTopicsFormDialogData {
	mode: DialogMode.ADD | DialogMode.EDIT;
	volunteerId: number;
	volunteerTopicId?: number;
	readonly: boolean;
}

interface VolunteerTopicFormFields {
	topicId: number,
	isApproved: boolean,
	note: string | undefined,
}

@Component({
	selector: 'volunteer-topics-form-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		CommonSelectFieldWidget,
		CommonTextFieldWidget,
		CommonButtonRowWidget
	],
	templateUrl: './volunteer-topics-form.dialog.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	"styles": [
		`
			.white-box {
				background-color: #eeeeee;
				padding: 1em;
				margin-bottom: 0.5em;
			}
		`
	]
})

export class VolunteerTopicsFormDialog implements OnInit, AfterViewInit {

	public dialogRef = inject(MatDialogRef<VolunteerTopicsFormDialog>);
	public dialogContext: DialogContext<VolunteerTopicsFormDialogData> = inject(MAT_DIALOG_DATA);

	public formSubmitButton = viewChild.required<ElementRef<HTMLButtonElement>>('topicSaveBtn');
	private readonly service = inject(VolunteerDialogDataService);
	private readonly domainData = inject(DomainDataService);

	private readonly form = new CommonForm<VolunteerTopicFormFields>(inject(Injector));
	public readonly dialogMode = DialogMode;


	private readonly isEnabledTopic = computed(() => {
		const data = this.dialogContext.data;
		if (!data || data.readonly) return false;

		return data.mode === DialogMode.ADD;
	});

	public readonly headerTitle = computed(() => {
		const data = this.dialogContext.data;
		if (!data) return 'View Topic Approval';

		if (data.mode == DialogMode.ADD) return 'Add Topic Approval';
		if (data.mode == DialogMode.EDIT) return 'Edit Topic Approval';
		return 'View Topic Approval';
	});

	public values = signal<VolunteerTopicFormFields | undefined>(undefined);

	private readonly topicsOptions = computed(() => {
		const volunteerTopicIds = new Set(this.service.volunteerTopics().map(t => t.topicId));
		const topics = this.domainData.data().topics.filter(topic => topic.enabled && topic.needsSpecialist).map(type => ({ text: type.name, value: type.topicId, }));

		// if in edit mode, include all topics or filter out topics which are already volunteerTopics
		return this.dialogContext.data?.mode === DialogMode.EDIT ? topics : topics.filter(topic => !volunteerTopicIds.has(topic.value));
	});
	readonly topic = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Topic', options: this.topicsOptions(), isRequired: true, isSpanish: false,
			isEnabled: this.isEnabledTopic,
		}),
		actualValue: computed(() => this.values()?.topicId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'topicId',
		form: this.form,
	}));

	private readonly statusOptions = computed(() => {
		return [
			{ value: false, text: 'Not Approved' },
			{ value: true, text: 'Approved' }
		];
	});
	readonly status = computed<CommonSelectField<boolean | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<boolean>>({
			label: 'Status', options: this.statusOptions(), isRequired: true, isSpanish: false,
		}),
		actualValue: computed(() => this.values()?.isApproved),
		pendingValue: signal<boolean | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'isApproved',
		form: this.form,
	}));

	private isNoteRequired = computed(() => {
		const status = this.status();
		const pendingValue = status.pendingValue();
		const actualValue = status.actualValue();

		const statusValue = pendingValue ? pendingValue : actualValue;

		return statusValue === false;
	});

	readonly note = computed<CommonTextField>(() => ({
		config: signal({ label: 'Note', min: this.isNoteRequired() ? 1 : 0, fixCase: 'sentence-case', isRequired: this.isNoteRequired(), multiLine: false, isSpanish: false, isEnabled: computed(() => !this.dialogContext.data?.readonly) }),
		actualValue: computed(() => this.values()?.note || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'note',
		form: this.form,
	}));

	async ngOnInit(): Promise<void> {
		this.form.resetAll();
		const data = this.dialogContext.data;

		if (data?.mode === DialogMode.EDIT) {
			if (data.volunteerTopicId) {
				const volunteerTopic = await this.service.getTopicById(data.volunteerTopicId);
				if (volunteerTopic) {
					this.values.set({ ...volunteerTopic });
				}
			}
		}
	}

	ngAfterViewInit() {
		this.form.setSubmitButton(
			this.formSubmitButton(),
			async (values: VolunteerTopicFormFields, changed: string[]) => {
				await this.onSaveInteraction(values);
			}
		);
	}

	async onSaveInteraction(values: VolunteerTopicFormFields) {
		const data = this.dialogContext.data;
		if (!data || data.readonly) return;

		const payload: VolunteerTopicForm = { ...values, volunteerId: data.volunteerId };

		let volunteerTopicId: number | undefined;
		if (data.mode === DialogMode.ADD) {
			volunteerTopicId = await this.service.addVolunteerTopic(payload);
		} else {
			volunteerTopicId = await this.service.updateVolunteerTopic({ ...payload, volunteerTopicId: data.volunteerTopicId });
		}
		this.closeDialog();
	}

	async closeDialog() {
		this.dialogRef.close();
	}


}